import {
  useState,
  useContext,
  createContext,
  ReactNode,
  MouseEvent,
} from "react";
import ms from "ms";
import styled, { keyframes, css } from "styled-components/macro";
import Portal from "../components/Portal";
import { Close } from "../components/Icons";
import { THEME_COLOR_DARK, BORDER_RADIUS } from "../vars";
import classNames from "classnames";
import Stack from "../components/Stack";

type Options = {
  success?: boolean;
  error?: boolean;
  warning?: boolean;
  white?: boolean;
  onClick?: (e: MouseEvent) => void;
};
type TToast = { id: string; toast: ReactNode; exit?: boolean } & Options;
type Context = {
  showToast: (t: ReactNode, options?: Options) => void;
};
const context = createContext<Context | null>(null);

export function Provider({ children }: { children: ReactNode }) {
  const [toasts, setToasts] = useState<TToast[]>([]);

  function showToast(toast: ReactNode, options: Options = {}) {
    const id = String(Math.random() + Date.now());
    setTimeout(() => {
      removeToast(id);
    }, ms("8s"));

    setToasts([...toasts, { toast, id, ...options }]);
  }

  function removeToast(id: string) {
    setToasts((toasts) => {
      return toasts.map((item) =>
        item.id !== id ? item : { ...item, exit: true }
      );
    });

    setTimeout(() => {
      setToasts((toasts) => {
        return toasts.filter((item) => item.id !== id);
      });
    }, 200);
  }

  return (
    <context.Provider value={{ showToast }}>
      {children}
      <Portal>
        <ToastsContainer>
          <Stack>
            {toasts.map(
              (
                {
                  toast,
                  id,
                  exit,
                  success,
                  warning,
                  error,
                  white,
                  onClick,
                  ...props
                },
                index
              ) => (
                <ToastsStub
                  key={id}
                  className={classNames({
                    exit,
                    show: !exit && index === toasts.length - 1,
                  })}
                >
                  <Toast
                    className={classNames({ success, warning, error, white })}
                    {...props}
                    as={onClick ? "button" : undefined}
                    clickable={Boolean(onClick)}
                    onClick={
                      onClick
                        ? (e: MouseEvent) => {
                            onClick(e);
                            removeToast(id);
                          }
                        : undefined
                    }
                  >
                    <div>{toast}</div>
                    <CloseButton onClick={() => removeToast(id)}>
                      <Close />
                    </CloseButton>
                  </Toast>
                </ToastsStub>
              )
            )}
          </Stack>
        </ToastsContainer>
      </Portal>
    </context.Provider>
  );
}

export function useToast() {
  const { showToast } = useContext(context)!;
  return showToast;
}

const StubShowKeyframes = keyframes`
  from {
    transform: scale(.9);
    opacity: 0;
  }
  to {
    transform: translateY(0) scale(1);
    opacity: 1;
  }
`;

const StubHideKeyframes = keyframes`
  from {
    transform: translateY(0) scale(1);
    opacity: 1;
  }
  to {
    transform: scale(.95);
    opacity: 0;
  }
`;

const ToastsStub = styled.div`
  display: flex;
  flex-direction: row-reverse;
  width: 100%;
  pointer-events: none;
  &.exit {
    transition-duration: 0.2s;
    animation: ${StubHideKeyframes} 0.2s;
    animation-fill-mode: forwards;
  }
  &.show {
    transition-duration: 0.2s;
    animation: ${StubShowKeyframes} 0.2s;
    animation-fill-mode: forwards;
  }
`;

const ToastsContainer = styled.div`
  position: fixed;
  bottom: 0;
  left: 0;
  min-width: 400px;
  padding: 16px;
  z-index: 10000;
  pointer-events: none;
`;

const Toast = styled.div<{ clickable: boolean }>`
  width: 100%;
  max-width: 400px;
  color: white;
  background: ${THEME_COLOR_DARK};
  border: 0;
  display: flex;
  justify-content: center;
  align-items: center;
  flex-direction: row;
  padding: 8px 16px;
  pointer-events: auto;
  margin: auto;
  border-radius: 3px;
  box-shadow: 0 0 0px 1px rgba(0, 0, 0, 0.1), 0 3px 9px rgba(0, 0, 0, 0.1),
    0 0 0 1px rgba(0, 0, 0, 0.1) inset;
  & > div:first-child {
    flex-grow: 1;
    text-align: left;
  }
  &.success {
    background: #72bf6f;
  }
  &.warning {
    background: #f6ab2f;
  }
  &.error {
    background: #e6492d;
  }
  &.white {
    border-radius: ${BORDER_RADIUS};
    background: white;
    box-shadow: 0 0 0px 1px rgba(0, 0, 0, 0.1), 0 1.5px 3px rgba(0, 0, 0, 0.1);
    font-size: 14px;
  }
  ${(p) =>
    p.clickable &&
    css`
      &:active {
        opacity: 0.3;
      }
    `}
`;

const CloseButton = styled.button`
  display: flex;
  background: transparent;
  border: 0;
  padding: 0;
  margin: 0;
  padding: 8px;
  justify-content: center;
  align-items: center;
  width: 32px;
  height: 32px;
  border-radius: 1000px;
  transition-duration: 0.2s;
  ${Close} {
    color: white;
    height: 12px;
  }
  &:hover {
    background: rgba(64, 64, 64, 0.1);
  }
  &:active {
    background: rgba(64, 64, 64, 0.2);
    color: #ffffff88;
  }
`;
